import Button from '@/components/app/Button';
import { Alert } from '@/components/ui/alert';
import { cn } from '@/components/ui/cn';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Box, Stack } from '@/components/ui/layout';
import { Text as NativeText } from '@/components/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import layoutStyles from './complete-password-reset-form.layout.module.css';

import { useCompletePasswordResetMutation } from '../api/password-reset/complete-password-reset';
import { validatePassword } from '../utils/password/validate-password';
import { PasswordRequirementChecklist } from './password-requirement-checklist';

const passwordResetFormSchema = z
  .object({
    confirmPassword: z
      .string()
      .min(1, { message: 'Password confirmation is required' }),
    password: z
      .string()
      .min(1, { message: 'Password is required' })
      .superRefine((value, ctx) => {
        try {
          validatePassword(value);
        } catch (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: (error as Error).message,
          });
        }
      }),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The passwords do not match',
        path: ['confirmPassword'],
      });
    }
  });

type PasswordResetFormData = z.infer<typeof passwordResetFormSchema>;

export function CompletePasswordResetForm({ tokenId }: { tokenId: string }) {
  const navigate = useNavigate();
  const { mutate: completePasswordReset, isPending: isCompletePending } =
    useCompletePasswordResetMutation();
  const { control, formState, handleSubmit, setError } =
    useForm<PasswordResetFormData>({
      defaultValues: {
        confirmPassword: '',
        password: '',
      },
      resolver: zodResolver(passwordResetFormSchema),
    });
  const password = useWatch({
    control,
    name: 'password',
  });
  const confirmPassword = useWatch({
    control,
    name: 'confirmPassword',
  });

  const isLoading = formState.isSubmitting || isCompletePending;

  function redirectToResetDestination(redirectPath: string) {
    if (typeof window !== 'undefined') {
      window.location.replace(redirectPath);
      return;
    }

    navigate(redirectPath, { replace: true });
  }

  const onSubmit = handleSubmit(({ confirmPassword, password }) => {
    completePasswordReset(
      {
        confirmPassword,
        password,
        token: tokenId,
      },
      {
        onError(error) {
          const message =
            error instanceof Error || isAxiosError(error)
              ? error.message
              : 'Unable to complete the password reset.';

          setError('root.globalError', { message });
        },
        onSuccess(response) {
          redirectToResetDestination(response.redirectPath);
        },
      }
    );
  });

  return (
    <Box as="form" onSubmit={onSubmit} className={cn(layoutStyles.layout1)}>
      <Stack className={cn(layoutStyles.layout2)}>
        <Stack className={cn(layoutStyles.layout3)}>
          <NativeText as="h1" variant="h4" className={cn(layoutStyles.layout4)}>
            Set a new password
          </NativeText>
          <NativeText className={cn(layoutStyles.layout5)}>
            Choose a new password for your Minerva account. We&apos;ll sign you
            in automatically after the reset completes.
          </NativeText>
        </Stack>

        <Controller
          control={control}
          name="password"
          render={({ field, fieldState }) => (
            /*
             * Census sweep: the house `Field` owns the label/message rhythm
             * (the MUI field's floated label reserved no layout space), and
             * the `data-testid` rides the control natively instead of MUI's
             * `inputProps` bucket.
             */
            <Field
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              label="Password"
            >
              <Input
                {...field}
                data-testid="password-reset-password-input"
                type="password"
              />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <Field
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              label="Confirm password"
            >
              <Input
                {...field}
                data-testid="password-reset-confirm-password-input"
                type="password"
              />
            </Field>
          )}
        />

        <PasswordRequirementChecklist
          confirmPassword={confirmPassword}
          password={password}
          revealValidationState={formState.submitCount > 0}
        />

        {formState.errors.root?.globalError?.message && (
          <Alert severity="error">
            {formState.errors.root.globalError.message}
          </Alert>
        )}

        <Button
          data-testid="password-reset-complete-button"
          type="submit"
          loading={isLoading}
          loadingText="Updating password..."
        >
          Update password
        </Button>
      </Stack>
    </Box>
  );
}
